import { useEffect, useState } from "react";

import { supabase } from "~/integrations/supabase/client";

export interface ClientSuggestion {
  id: string;
  name: string;
  taxId?: string;
}

export interface SalesRequestSuggestion {
  id: string;
  clientId: string;
  totalAmount: number;
  paymentDueDate: string;
  status: string;
}

export const useClientSuggestions = (paymentAmount: number) => {
  const [clientSuggestions, setClientSuggestions] = useState<
    ClientSuggestion[]
  >([]);
  const [requestSuggestions, setRequestSuggestions] = useState<
    SalesRequestSuggestion[]
  >([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!paymentAmount) return;

      setLoadingSuggestions(true);

      try {
        // First, fetch all unpaid sales requests
        const { data: salesRequestsData, error: salesRequestsError } =
          await supabase
            .from("sales_request")
            .select("id, client_id, total_amount, payment_due_date, status")
            .in("status", ["pending", "prepaid", "accepted"])
            .eq("paid", false)
            .order("created_at", { ascending: false });

        if (salesRequestsError) throw salesRequestsError;

        console.log("Fetched sales requests:", salesRequestsData?.length || 0);

        // Convert to our format
        const formattedRequests: SalesRequestSuggestion[] = (
          salesRequestsData || []
        ).map((req) => ({
          id: req.id,
          clientId: req.client_id,
          totalAmount: req.total_amount,
          paymentDueDate: req.payment_due_date,
          status: req.status,
        }));

        setRequestSuggestions(formattedRequests);

        // Find requests with matching amounts (within 2%)
        const matchingAmountRequests = formattedRequests.filter(
          (req) =>
            req.totalAmount >= paymentAmount * 0.98 &&
            req.totalAmount <= paymentAmount * 1.02
        );

        // Get unique client IDs from matching requests
        const matchingClientIds = [
          ...new Set(matchingAmountRequests.map((req) => req.clientId)),
        ];

        // If there are matching clients, fetch only those specific clients
        if (matchingClientIds.length > 0) {
          const { data: clientsData, error: clientsError } = await supabase
            .from("clients")
            .select("id, name, tax_id")
            .in("id", matchingClientIds)
            .order("name");

          if (clientsError) throw clientsError;

          const formattedClients: ClientSuggestion[] = (clientsData || []).map(
            (client) => ({
              id: client.id,
              name: client.name,
              taxId: client.tax_id,
            })
          );

          setClientSuggestions(formattedClients);
        } else {
          // No matching clients, just return an empty array
          setClientSuggestions([]);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setClientSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    fetchSuggestions();
  }, [paymentAmount]);

  return { clientSuggestions, requestSuggestions, loadingSuggestions };
};
